import { unwrap } from "@sagot/api-client";
import { useInfiniteQuery, useQuery, useQueryClient, type InfiniteData, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { isProcessing } from "../shared/ingestion";
import { dkKeys } from "../shared/keys";
import type { AccessMatrixOut, ChunkView, SourceUsage, SourceView, VersionView } from "../shared/types";

export const CHUNK_PAGE_SIZE = 20;
const FALLBACK_POLL_MS = 5_000;

/** Polls while processing only when the live SSE stream is down (CLAUDE.md rule 8 fallback). */
export function useSource(sourceId: string, streamLive: boolean): UseQueryResult<SourceView> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: dkKeys.source(wsId, sourceId),
    queryFn: () => unwrap(api.GET("/v1/sources/{source_id}", { params: { path: { source_id: sourceId } } })),
    refetchInterval: (query) => (!streamLive && isProcessing(query.state.data?.status ?? "") ? FALLBACK_POLL_MS : false),
  });
}

export function useSourceUsage(sourceId: string): UseQueryResult<SourceUsage> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: dkKeys.usage(wsId, sourceId),
    queryFn: () => unwrap(api.GET("/v1/sources/{source_id}/usage", { params: { path: { source_id: sourceId } } })),
  });
}

export function useSourceVersions(sourceId: string): UseQueryResult<VersionView[]> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: dkKeys.versions(wsId, sourceId),
    queryFn: () => unwrap(api.GET("/v1/sources/{source_id}/versions", { params: { path: { source_id: sourceId } } })),
  });
}

/** `inherit_audiences` is only exposed by the access matrix (needs `audiences.view`); optional. */
export function useAccessMatrix(): UseQueryResult<AccessMatrixOut> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: dkKeys.accessMatrix(wsId), queryFn: () => unwrap(api.GET("/v1/access-matrix")), retry: false });
}

export function useChunks(sourceId: string, q: string) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useInfiniteQuery({
    queryKey: dkKeys.chunks(wsId, sourceId, q),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET("/v1/sources/{source_id}/chunks", {
          params: { path: { source_id: sourceId }, query: { q: q || undefined, limit: CHUNK_PAGE_SIZE, offset: pageParam } },
        }),
      ),
    getNextPageParam: (last, pages) => (last.length < CHUNK_PAGE_SIZE ? undefined : pages.length * CHUNK_PAGE_SIZE),
  });
}

/** Replaces one chunk in every cached chunk list of this source (any search). */
export function useReplaceCachedChunk(sourceId: string) {
  const queryClient = useQueryClient();
  const wsId = useWorkspaceId();
  return (chunk: ChunkView) => {
    queryClient.setQueriesData<InfiniteData<ChunkView[]>>({ queryKey: [...dkKeys.source(wsId, sourceId), "chunks"] }, (data) =>
      data ? { ...data, pages: data.pages.map((page) => page.map((row) => (row.id === chunk.id ? chunk : row))) } : data,
    );
  };
}

/** Invalidates everything the detail page shows (after re-index / restore / replace / access). */
export function useInvalidateSource(sourceId: string) {
  const queryClient = useQueryClient();
  const wsId = useWorkspaceId();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: dkKeys.source(wsId, sourceId) }),
      queryClient.invalidateQueries({ queryKey: dkKeys.sources(wsId), exact: true }),
      queryClient.invalidateQueries({ queryKey: dkKeys.accessMatrix(wsId) }),
    ]);
  };
}
